import { router } from 'expo-router';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { colors } from '@hogar/shared';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Button, Card, ErrorText, Field, Label, Screen, Segmented, Txt, useT } from '../components/ui';
import { LanguagePicker } from '../components/LanguagePicker';
import { Logo } from '../components/Logo';

export default function SignIn() {
  const { t, lang } = useT();
  const { signIn, setLang } = useSession();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const explain = (e: unknown) => {
    const c = e instanceof ApiError ? e.code : '';
    if (c === 'wrong_code') return t('errWrongCode');
    if (c === 'code_expired') return t('errCodeExpired');
    if (c === 'too_many_codes') return t('errTooMany');
    if (c === 'too_many_requests') return t('errTooManyRequests');
    if (c === 'email_ceiling') return t('errEmailCeiling');
    if (c === 'offline') return t('errOffline');
    if (c.startsWith('invalid_input')) return t('errEmail');
    return t('errGeneric');
  };

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api<{ ok: true; devCode?: string }>('POST', '/auth/start', { email, lang });
      setSentTo(email.trim().toLowerCase());
      setDevCode(r.devCode ?? null);
      setCode('');
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api<{ token: string }>('POST', '/auth/verify', { email: sentTo, code, lang });
      await signIn(r.token);
    } catch (e) {
      setError(explain(e));
      setBusy(false);
    }
  };

  return (
    <Screen>
      <View style={{ alignItems: 'flex-end', marginBottom: 8 }}>
        <View style={{ width: 200 }}>
          <LanguagePicker value={lang} options={['es', 'en'] as const} onChange={setLang} />
        </View>
      </View>
      <View style={{ alignItems: 'center', marginVertical: 28 }}>
        <Logo />
      </View>
      <Txt weight="semibold" size={24}>
        {t('welcome')}
      </Txt>
      <Txt color={colors.textMuted} style={{ marginTop: 4 }}>
        {t('noPassword')}
      </Txt>

      {sentTo === null ? (
        <>
          <Label>{t('emailLabel')}</Label>
          <Field value={email} onChange={setEmail} keyboardType="email-address" autoComplete="email" placeholder={t('emailPlaceholder')} onSubmit={send} autoFocus />
          <ErrorText>{error}</ErrorText>
          <Button label={t('sendCode')} onPress={send} disabled={busy || !email.includes('@')} style={{ marginTop: 18 }} />
        </>
      ) : (
        <>
          <Txt style={{ marginTop: 18 }}>{t('codeSent', { email: sentTo })}</Txt>
          {devCode ? (
            <Card style={{ marginTop: 12, paddingVertical: 12, backgroundColor: colors.warningSoft, borderColor: colors.warningSoft }}>
              <Txt size={14}>{t('devCode', { code: devCode })}</Txt>
            </Card>
          ) : null}
          <Label>{t('codeLabel')}</Label>
          <Field value={code} onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))} keyboardType="number-pad" autoComplete="one-time-code" maxLength={6} placeholder="123456" onSubmit={verify} autoFocus />
          <ErrorText>{error}</ErrorText>
          <Button label={t('continue')} onPress={verify} disabled={busy || code.length !== 6} style={{ marginTop: 18 }} />
          <Pressable onPress={() => setSentTo(null)} style={{ alignItems: 'center', padding: 16 }}>
            <Txt color={colors.primary} weight="medium">
              {t('otherEmail')}
            </Txt>
          </Pressable>
        </>
      )}
      <Pressable onPress={() => router.push({ pathname: '/help', params: { a: 'sign-in-code' } })} style={{ alignItems: 'center', padding: 16, marginTop: 8 }}>
        <Txt color={colors.textMuted} weight="medium">
          {t('help')}
        </Txt>
      </Pressable>
    </Screen>
  );
}
